import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, FormField, Input, useToast } from '@vd/ui';
import { CheckCircle2, Chrome } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../features/auth/AuthProvider';
import { EVERY_SCREEN, ONBOARDING_SCREEN } from '../lib/screenNames';
import styles from './OnboardingPage.module.css';

const steps = [
  { key: 'about',    title: 'Tell us about yourself',  desc: 'A quick four-question intro so we can tailor the product to you.' },
  { key: 'install',  title: 'Install the extension',   desc: 'Install Toado from the Chrome Web Store.' },
  { key: 'company',  title: 'Create a company',        desc: 'Your tickets live under a company. You can create more later.' },
  { key: 'project',  title: 'Create your first project', desc: "We'll auto-assign captures from this URL to this project." },
  { key: 'capture',  title: 'Try your first capture',  desc: 'Open any page, click the Toado icon on your extensions bar.' },
  { key: 'mcp',      title: 'Connect via MCP',         desc: 'Toado runs an MCP server so Claude Code, Cursor, or any MCP client can read tickets and file new ones from your editor.' },
];

const ROLE_OPTIONS = [
  'Vibe coder',
  'Developer / Engineer',
  'Designer',
  'Product manager',
  'Founder / CEO',
  'Engineering manager',
  'QA / tester',
  'Other',
];
/**
 * What is stored keeps the form people have already answered in, so earlier
 * and later answers still group together; only what the select shows reads
 * "to" instead of a dash.
 */
const EXPERIENCE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Just getting started', label: 'Just getting started' },
  { value: '1–2 years', label: '1 to 2 years' },
  { value: '3–5 years', label: '3 to 5 years' },
  { value: '5–10 years', label: '5 to 10 years' },
  { value: '10+ years', label: '10+ years' },
];
const PRIMARY_USE_OPTIONS = [
  'Vibe coding workflow',
  'Personal bug tracking',
  'Team bug management',
  'Design QA / feedback',
  'AI agent collaboration (MCP)',
  'Other',
];
const REFERRAL_OPTIONS = [
  'Search engine (Google, etc.)',
  'X / Twitter',
  'LinkedIn',
  'Hacker News',
  'Reddit',
  'YouTube',
  'A blog or article',
  'Friend or colleague',
  'AI assistant (ChatGPT, Claude, etc.)',
  'Other',
];

export function OnboardingPage() {
  const [step, setStep] = useState(0);
  const [companyName, setCompanyName] = useState('');
  const [projectName, setProjectName] = useState('');
  const [urlPattern, setUrlPattern] = useState('');
  const [role, setRole] = useState('');
  const [experience, setExperience] = useState('');
  const [primaryUse, setPrimaryUse] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [stepBusy, setStepBusy] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { currentCompanyId, refreshUser, setCurrentCompany } = useAuth();

  const current = steps[step]!;
  const aboutComplete = !!(role && experience && primaryUse && referralSource);
  const companyComplete = companyName.trim().length > 0;
  const projectComplete = projectName.trim().length > 0;

  // Submit the questionnaire when leaving the "about" step. The submit
  // failing shouldn't strand the user — log the answers in memory and
  // continue regardless.
  const advanceFromAbout = async () => {
    setProfileSaving(true);
    try {
      await api.saveOnboardingProfile({ role, experience, primaryUse, referralSource });
    } catch {
      /* swallow — onboarding continues so the user isn't stranded on a
         retryable analytics failure */
    } finally {
      setProfileSaving(false);
    }
    setStep((s) => s + 1);
  };

  // Create the company on the company step — the wizard collects a name
  // on screen but only persists when the user clicks Continue. Sets it as
  // the currentCompany so the project step can immediately create a project
  // under it. If the user already has a company (e.g. came in via an
  // invite acceptance and reopened onboarding), we no-op here.
  const advanceFromCompany = async () => {
    if (currentCompanyId) {
      setStep((s) => s + 1);
      return;
    }
    setStepBusy(true);
    try {
      const created = await api.createCompany({ name: companyName.trim() });
      await refreshUser();
      setCurrentCompany(created.id);
      setStep((s) => s + 1);
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Could not create company', description: (e as Error).message });
    } finally {
      setStepBusy(false);
    }
  };

  const advanceFromProject = async () => {
    if (!currentCompanyId) {
      // Shouldn't happen — company step ran before this. Fall through
      // rather than block the wizard so the user isn't stranded.
      setStep((s) => s + 1);
      return;
    }
    setStepBusy(true);
    try {
      await api.createProject(currentCompanyId, {
        name: projectName.trim(),
        urlPattern: urlPattern.trim() || undefined,
      });
      setStep((s) => s + 1);
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Could not create project', description: (e as Error).message });
    } finally {
      setStepBusy(false);
    }
  };

  const onContinue = () => {
    if (current.key === 'about') {
      if (!aboutComplete) return; // button is disabled, but defense-in-depth
      return void advanceFromAbout();
    }
    if (current.key === 'company') {
      if (!companyComplete && !currentCompanyId) return;
      return void advanceFromCompany();
    }
    if (current.key === 'project') {
      if (!projectComplete) return;
      return void advanceFromProject();
    }
    setStep((s) => s + 1);
  };

  return (
    <div className={styles.shell}>
      {/* Each step is a screen for pins (TOADO-701), all at this one address;
          the brand and the progress dots are on every one. */}
      <div className={styles.card} data-toado-view={`${ONBOARDING_SCREEN}/${current.key}`}>
        <div className={styles.brand} data-toado-view={EVERY_SCREEN}>
          <img src="/favicon.svg" alt="" className={styles.brandMark} />
          <span className={styles.brandName}>Toado</span>
        </div>
        <div className={styles.pips} data-toado-view={EVERY_SCREEN}>
          {steps.map((_s, i) => (
            <span key={i} className={i <= step ? styles.pipActive : styles.pip} />
          ))}
        </div>
        <h1 className={styles.title}>{current.title}</h1>
        <p className={styles.desc}>{current.desc}</p>

        <div className={styles.body}>
          {current.key === 'about' && (
            <div className={styles.questionList}>
              <FormField label="What do you do?">
                <select className={styles.select} value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="">Select…</option>
                  {ROLE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </FormField>
              <FormField label="How much experience do you have?">
                <select className={styles.select} value={experience} onChange={(e) => setExperience(e.target.value)}>
                  <option value="">Select…</option>
                  {EXPERIENCE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </FormField>
              <FormField label="What's your primary reason for using Toado?">
                <select className={styles.select} value={primaryUse} onChange={(e) => setPrimaryUse(e.target.value)}>
                  <option value="">Select…</option>
                  {PRIMARY_USE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </FormField>
              <FormField label="Where did you hear about us?">
                <select className={styles.select} value={referralSource} onChange={(e) => setReferralSource(e.target.value)}>
                  <option value="">Select…</option>
                  {REFERRAL_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </FormField>
            </div>
          )}
          {current.key === 'project' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <FormField label="Project name">
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Checkout"
                />
              </FormField>
              <FormField label="URL pattern (optional)" help="We'll auto-assign captures from URLs matching this pattern to this project.">
                <Input
                  value={urlPattern}
                  onChange={(e) => setUrlPattern(e.target.value)}
                  placeholder="https://acme.example.com/checkout/*"
                />
              </FormField>
            </div>
          )}
          {current.key === 'install' && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
              <a
                href="https://chromewebstore.google.com/detail/toado/lmmeeckpnachbbkilafepggbnnjhmkaj"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="primary" leftIcon={<Chrome size={14} />}>
                  Install from Chrome Web Store
                </Button>
              </a>
            </div>
          )}
          {current.key === 'capture' && (
            <div className={styles.extBar} aria-label="Browser extensions bar with the Toado icon highlighted">
              <span className={styles.extDot} />
              <span className={styles.extDot} />
              <span className={styles.extDot} />
              <span className={styles.extToado}>
                <img src="/favicon.svg" alt="" className={styles.extToadoIcon} />
              </span>
              <span className={styles.extDot} />
            </div>
          )}
          {current.key === 'mcp' && (
            <div className={styles.mcpBlock}>
              <p className={styles.mcpIntro}>
                Connect Claude Code to Toado in two steps. The first registers the server; the second authenticates with OAuth.
              </p>
              <div className={styles.mcpClient}>
                <div className={styles.mcpClientName}>1. Register the server</div>
                <div className={styles.mcpClientHint} style={{ marginBottom: 8 }}>
                  Run this from any terminal. The <code>--scope user</code> flag makes Toado available across all your projects.
                </div>
                <code className={styles.mcpCode}>claude mcp add --transport http --scope user toado https://mcp.toado.dev/mcp</code>
              </div>
              <div className={styles.mcpClient}>
                <div className={styles.mcpClientName}>2. Authenticate via /mcp</div>
                <div className={styles.mcpClientHint}>
                  Open a Claude Code session and run <code>/mcp</code>. A browser tab opens for OAuth approval; pick your scope and approve.
                </div>
                <ul style={{ color: 'var(--text-secondary)', fontSize: 13, paddingLeft: 18, lineHeight: 1.7, margin: '8px 0 0' }}>
                  <li>
                    <strong>VS Code or Cursor:</strong> open the Claude Code chat panel and type <code>/mcp</code> in the prompt.
                  </li>
                  <li>
                    <strong>Terminal:</strong> run <code>claude</code> in any directory, then type <code>/mcp</code> at the prompt.
                  </li>
                </ul>
              </div>
              <p style={{ color: 'var(--text-tertiary)', fontSize: 12, margin: '12px 0 0' }}>
                Using Claude Desktop, Grok, Cursor's native MCP, Codex, or another client?{' '}
                <a href="/me/settings/mcp-setup" className={styles.mcpLink}>See full setup options</a>.
              </p>
            </div>
          )}
          {current.key === 'company' && (
            <FormField label="Company name">
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Acme Co."
              />
            </FormField>
          )}
        </div>

        <div className={styles.footer}>
          {/* The "about" step is required — no Skip, no Back (it's first
              anyway). Steps 1+ get a Back button so the user can go back
              to review answers. Skipping onboarding entirely is gone:
              once we've gated on About, we want users to finish the
              walkthrough rather than bail out half-configured. */}
          {current.key !== 'about' && (
            <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>Back</Button>
          )}
          <div style={{ flex: 1 }} />
          {step < steps.length - 1 ? (
            <Button
              variant="primary"
              onClick={onContinue}
              loading={profileSaving || stepBusy}
              disabled={
                (current.key === 'about' && !aboutComplete) ||
                (current.key === 'company' && !companyComplete && !currentCompanyId) ||
                (current.key === 'project' && !projectComplete)
              }
            >
              Continue
            </Button>
          ) : (
            <Button variant="primary" leftIcon={<CheckCircle2 size={14} />} onClick={() => navigate('/')}>
              Finish
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
